/**
 * Homepage v2 colour mapping (design doc §5.5).
 *
 * Every colour here maps to an existing `proda-*` design token — the board
 * introduced no new palette. Kept in one module, next to
 * `formatHomepageRowCopy`, so the column and row components stay presentational:
 * they ask what tint/indicator a thing has rather than each holding their own
 * copy of the table.
 *
 * A row's `FileStatus` is deliberately never rendered as a badge on the board
 * (§5.5) — the column tint plus the copy lines carry the meaning.
 */
import type { THomepageColumnKey, THomepageRow } from "@/store";

export type THomepageColumnTint = {
    /** Applied to the column header strip. */
    header: string;
    /** Applied to every card in the column. */
    card: string;
    /** Applied on hover to a *clickable* card only: the same tint, deep enough
     * to be unmistakable against the resting state. A card the server marked
     * unclickable keeps its resting colour, so hover never implies an action
     * that is not there.
     *
     * Empty for a column whose rows are never clickable at all
     * (`waiting_on_service_provider`, §6) — there is no hover state to define. */
    cardHover: string;
};

/**
 * Column tints, per §5.5's table: the colour belongs to the header **and the
 * cards**, never to the column behind them, which stays white. Tinting the
 * backdrop instead leaves white cards floating on colour — the inverse of the
 * mocks, and it drains the contrast the cards need to read as separate items.
 *
 * The two neutral columns carry the same grey as their header rather than a
 * colour: nothing about them is urgent, but the card still has to separate
 * itself from the white column behind it.
 */
const COLUMN_TINTS: Record<THomepageColumnKey, THomepageColumnTint> = {
    not_yet_due: { header: "bg-muted/60", card: "bg-muted/60", cardHover: "hover:bg-muted" },
    // Never clickable, by server decision (`_is_clickable`): the accountant is
    // waiting, not acting, so the card has no hover state at all.
    waiting_on_service_provider: {
        header: "bg-muted/60",
        card: "bg-muted/60",
        cardHover: "",
    },
    upload_now: {
        header: "bg-proda-warning/10",
        card: "bg-proda-warning/10",
        cardHover: "hover:bg-proda-warning/30",
    },
    ready_for_you: {
        header: "bg-proda-lavender/10",
        card: "bg-proda-lavender/10",
        cardHover: "hover:bg-proda-lavender/30",
    },
    submitted: {
        header: "bg-proda-success/10",
        card: "bg-proda-success/10",
        cardHover: "hover:bg-proda-success/30",
    },
    submitted_to_erp: {
        header: "bg-proda-success/10",
        card: "bg-proda-success/10",
        cardHover: "hover:bg-proda-success/30",
    },
};

export function homepageColumnTint(columnKey: THomepageColumnKey): THomepageColumnTint {
    return COLUMN_TINTS[columnKey];
}

/**
 * A row's timeliness indicator.
 *
 * - `on_time` / `late` — a satisfied row, judged against its deadline. These
 *   are the columns whose cards carry a leading check (§7.1).
 * - `null` — nothing to badge. An unsatisfied row past its deadline used to be
 *   flagged with a clock here; it no longer is (§5.6) — its copy line already
 *   says how late it is, and an icon on every such card was pure noise.
 */
export type THomepageRowTimeliness = "on_time" | "late" | null;

const SUBMITTED_COLUMNS = new Set<THomepageColumnKey>(["submitted", "submitted_to_erp"]);

export function homepageRowTimeliness(
    row: THomepageRow,
    columnKey: THomepageColumnKey,
): THomepageRowTimeliness {
    if (SUBMITTED_COLUMNS.has(columnKey)) {
        // `submittedOnTime` is null when nothing satisfying was uploaded, which
        // cannot happen in these columns — treated as on time rather than
        // accusing a row of lateness the server never claimed.
        return row.submittedOnTime === false ? "late" : "on_time";
    }
    return null;
}

/** Icon colour for a timeliness indicator, per §5.5's tokens. */
export function homepageTimelinessClass(timeliness: THomepageRowTimeliness): string {
    switch (timeliness) {
        case "on_time":
            return "text-proda-success";
        case "late":
            return "text-proda-orange";
        default:
            return "";
    }
}
